<template>
    <div class="book-insights-landing">
        <authentication-forms-modal @subscribe-to-newsletter="subscribeToNewsLetter" />
        <main>
            <section class="landing-hero">
                <div class="container">
                    <div class="row">
                        <div class="col-12 col-lg-6 d-flex flex-column align-items-center">
                            <img src="@assets/img/book-insights/insights-header.svg" alt="hibooks logo">
                            <hr>
                            <p>Book Insights are now on Memo’d:<br>even more inspiring ideas but quicker.</p>
                            <a
                                href="https://memod.com/tags/bookinsights/trending"
                                target="_blank"
                                class="btn btn-primary"
                            >
                                Read on Memo’d
                            </a>
                            <router-link class="discover-link" :to="{ name: 'bi-discover' }">
                                View Book Insights archives
                            </router-link>
                            <div class="listen-on-container">
                                <span>Listen to the Book Insights podcast on:</span>
                                <div class="listen-on">
                                    <a href="https://open.spotify.com/show/0yRt4S4QYio2I32neOw18i" target="_blank">
                                        <img src="@assets/img/book-insights/icons/spotify.svg" alt="Spotify">
                                    </a>
                                    <a href="https://www.youtube.com/channel/UCaWLtN8pRQE0o8ZNN4lxYRw" target="_blank">
                                        <img src="@assets/img/book-insights/icons/youtube.svg" alt="Youtube">
                                    </a>
                                    <a href="https://podcasts.apple.com/us/podcast/book-insights-podcast/id1510442256" target="_blank">
                                        <img src="@assets/img/book-insights/icons/audio.svg" alt="Apple">
                                    </a>
                                    <a href="https://podcasts.google.com/?feed=aHR0cHM6Ly9hbmNob3IuZm0vcy8xOTIzZjc2Yy9wb2RjYXN0L3Jzcw" target="_blank">
                                        <img src="@assets/img/book-insights/icons/gp.svg" alt="Google">
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section class="subscribe-newsletter">
                <div class="container">
                    <div class="row no-gutters">
                        <div class="col-12 col-md-4 d-flex align-items-center justify-content-center">
                            <p class="mb-0 mr-0 mr-md-2 mb-3 mb-md-0 text-center text-md-left">
                                Subscribe to our weekly email so you’ll never miss a Book Insight.
                            </p>
                        </div>
                        <div class="col-12 col-md">
                            <newsletter-box />
                        </div>
                    </div>
                </div>
            </section>
            <section class="bi-categories-carousel-container">
                <div class="container">
                    <h1>Insights from the world’s most impactful<br>nonfiction books, expertly distilled for you</h1>
                    <bi-categories-carousel :book-insights="bookInsights" />
                </div>
            </section>
            <section class="team-carousel-container">
                <div class="container">
                    <h1>Created by thought leaders,<br>researchers, and journalists</h1>
                    <div class="team-carousel">
                        <div class="swiper-button swiper-button-prev-team d-none d-sm-block" />
                        <div class="swiper-button swiper-button-next-team d-none d-sm-block" />
                        <swiper :options="swiperOptions">
                            <swiper-slide v-for="(collaborator, index) in collaborators" :key="index">
                                <collaborator-bio :collaborator="collaborator" />
                            </swiper-slide>
                        </swiper>
                    </div>
                    <div class="about-team">
                        <router-link
                            class="btn btn-primary"
                            type="button"
                            tag="button"
                            :to="{ name: 'insight-about-us' }"
                        >
                            Learn More About the Team
                        </router-link>
                    </div>
                </div>
            </section>
        </main>
        <memod-footer />
    </div>
</template>

<script>
import _get from "lodash/get";
import { mapState, mapGetters } from "vuex";
import store from "@/store";
import exampleBookInsightMixins from "@/mixins/exampleBookInsightMixins";
import "swiper/dist/css/swiper.css";
import { swiper, swiperSlide } from "vue-awesome-swiper";

export default {
    name: "BooksInsightsLanding",
    components: {
        swiper,
        swiperSlide,
        memodFooter: () => import(/* webpackChunkName: "memod-footer"*/ "@v/memod-footer"),
        biCategoriesCarousel: () => import(/* webpackChunkName: "bi-categories-carousel"*/ "./bi-categories-carousel/"),
        collaboratorBio: () => import(/* webpackChunkName: "collaborator-bio"*/ "@c/collaborator-bio"),
        authenticationFormsModal: () => import(/* webpackChunkName: "authentication-forms"*/ "@c/modals/authentication-forms"),
        newsletterBox: () => import(/* webpackChunkName: "newsletter-box" */ "@c/newsletter-box")
    },
    mixins: [exampleBookInsightMixins],
    data() {
        return {
            isLoading: false,
            mailchimp_list_id: process.env.VUE_APP_MAILCHIMP_LIST_ID,
            harcodedBookInsigthIds: [64, 93, 105, 145, 40],
            bookInsights: [],
            swiperOptions: {
                slidesPerView: 3,
                spaceBetween: 20,
                slidesPerGroup: 3,
                threshold: 50,
                navigation: {
                    nextEl: ".swiper-button-next-team",
                    prevEl: ".swiper-button-prev-team"
                },
                breakpoints: {
                    991: {
                        slidesPerView: 2,
                        slidesPerGroup: 1
                    },
                    767: {
                        spaceBetween: 10,
                        slidesPerView: 1,
                        slidesPerGroup: 1
                    }
                }
            }
        }
    },
    computed: {
        ...mapGetters({
            isLoggedIn: "User/isLoggedIn"
        }),
        ...mapState({
            collaborators: state => state.AboutTeam.collaborators,
            userData: state => state.User.data
        })
    },
    beforeRouteEnter(to, from, next) {
        const isBiSubscribed = localStorage.getItem("bi-subscribed");
        store.dispatch("Application/setSelectedNonfictionGift", {});
        if (isBiSubscribed) {
            return next({ name: "bi-discover" });
        }

        next();
    },
    created() {
        this.$_getExampleBookInsights(this.harcodedBookInsigthIds);
    },
    methods:{
        newsletterValidation() {
            if (this.isLoggedIn) {
                this.subscribeToNewsLetter();
            } else {
                this.$modal.show("authentication-forms-modal", { params: { formType: "sign-up" } })
            }
        },
        subscribeToNewsLetter() {
            if (this.isLoading) {
                return;
            }
            const user = this.userData;
            const email = _get(user, "user.email_address", "") || _get(user, "facebook.email_address", "") || _get(user, "email", "");
            this.isLoading = true;

            this.sendTrackings();
            axiosMemod({
                url: `/newsletter`,
                method: "POST",
                data: {
                    email,
                    list_id: this.mailchimp_list_id
                }
            }).then(() => {
                localStorage.setItem("bi-subscribed", true);
                this.$router.push({ name: "bi-discover", params: { sourceRoute: "newsLetterSubscription" } });
            }).catch(() => {
                this.$notify({
                    group: "app-notifications",
                    type: "error",
                    text: "Something went wrong!.",
                    duration: 5000
                });
            }).finally(() => {
                this.isLoading = false;
            })
        },
        sendTrackings() {
            this.$_trackAll("bi-conv-landing-fnsu-atf", {
                action: "fnsu-atf"
            });
            this.$_gtmTrackEvent({
                event: "bi-conv-landing-fnsu-atf",
                action: "fnsu-atf"
            });
        },
        setSubscribeBtnVisibility(visibility = false) {
            this.$store.dispatch("Application/setSubscribeBtnVisibility", visibility);
        }
    }
}
</script>

<style lang="scss">
.book-insights-landing {
    .landing-hero {
        background-image: url("~@assets/img/book-insights/landing-bg.jpg");
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
        background-color: $primary-color;
        position: relative;
        height: calc(100vh - 83px);
        min-height: 500px;
        max-height: 900px;
        display: flex;
        align-items: center;

        &::before {
            content: "";
            position: absolute;
            top: -1px;
            left: -1px;
            right: -1px;
            bottom: -1px;
            background: rgb(0,0,0);
            background: linear-gradient(0deg, rgba(2,2,2,1) 0%, rgba(0,0,0,0.3) 50%, rgba(2,2,2,1) 100%);
        }

        @media (max-width: $md) {
            height: calc(100vh - 118px);
            overflow: hidden;
        }

        @media (max-width: 475px) {
            height: calc(100vh - 137px);
            overflow: hidden;
        }

        @media (max-height: 670px) and (max-width: $md) {
            min-height: 620px;
        }

        hr {
            max-width: 400px;
            width: 90%;
            border-color: white;
            margin-top: 15px;
            margin-bottom: 30px;
        }

        p {
            color: white;
            font-size: 20px;
            font-weight: normal;
            line-height: 22px;
            margin-bottom: 30px;
            text-align: center;

            @media (max-width: $md) {
                font-size: 22px;
                font-weight: normal;
                font-style: normal;
                font-stretch: normal;
                line-height: 1.48;
                letter-spacing: 0.4px;
                text-align: center;
            }
        }

        span {
            display: block;
            color: #D3D2D2;
            font-size: 18px;
            margin-bottom: 30px;

            @media (max-width: $md) {
                text-align: center;
            }
        }

        .btn {
            margin-bottom: 30px;
            transform: scale(1.3);

            @media (max-width: $md) {
                transform: scale(1);
                width: 100%;
                margin-bottom: 35px;
            }
        }

        .discover-link {
            color: white;
            text-decoration: underline;
            margin-bottom: 25px;
        }

        .listen-on-container {
            display: flex;
            flex-direction: column;
            align-items: center;

            span {
                margin-bottom: 10px;
                font-weight: 500;
                font-size: 13px;
            }

            .listen-on {
                display: flex;

                a {
                    margin-left: 25px;
                    display: block;

                    &:first-child {
                        margin-left: 0;
                    }
                }

                @media (max-width: $md) {
                    justify-content: center;
                    margin-bottom: 50px;
                }
            }
        }
    }

    .subscribe-newsletter {
        background-color: $dark-gray;
        padding: 15px 0;

        .container {
            > .row {
                padding-left: 60px;
                padding-right: 60px;

                @media (max-width: $md) {
                    padding-left: 0;
                    padding-right: 0;
                }
            }
        }
    }

    .bi-categories-carousel-container {
        padding: 20px 0;

        h1 {
            font-size: 40px;
            font-weight: 500;
            line-height: 1.2;
            text-align: center;
            margin-bottom: 40px;

            @media (max-width: $md) {
                font-size: 20px;
                font-weight: 600;
                font-style: normal;
                font-stretch: normal;
                line-height: 1.45;
                letter-spacing: 0.4px;
                text-align: center;
            }
        }
    }

    .team-carousel-container {
        overflow: hidden;

        .container {
            padding-left: 20px;
            padding-right: 20px;
        }

        h1 {
            font-size: 40px;
            font-weight: 500;
            line-height: 1.2;
            text-align: center;
            margin-bottom: 40px;

            @media (max-width: $md) {
                font-size: 20px;
                font-weight: 600;
                font-style: normal;
                font-stretch: normal;
                line-height: 1.45;
                letter-spacing: 0.4px;
                text-align: center;
            }
        }

        .team-carousel {
            position: relative;
            margin-bottom: 40px;

            @media (max-width: $md) {
                margin-bottom: 20px;
            }

            .swiper-container {
                @media (max-width: $md) {
                    overflow: visible;
                }

                .collaborator {
                    margin-bottom: 0;
                    cursor: default;

                    span {
                        display: none;
                    }
                }
            }

            .swiper-button-prev-team, .swiper-button-next-team {
                /* background-color: $medium-gray; */
                width: 40px;
                height: 40px;
                background-image: url("~@assets/img/carousel-arrow-new.svg");
                background-position: center;
                background-repeat: no-repeat;
                box-shadow: 0 0 9px 0 rgba(0,0,0,0.1);
                top: 50%;
                position: absolute;
                margin-top: -20px;
                z-index: 10;
                cursor: pointer;

                &:focus {
                    outline: initial;
                }
            }

            .swiper-button-prev-team {
                transform: scaleX(-1);
                left: -45px;
            }

            .swiper-button-next-team {
                right: -45px;
            }
        }

        .about-team {
            display: flex;
            justify-content: center;
            margin-bottom: 80px;

            @media (max-width: $md) {
                button {
                    width: 100%;
                }
            }
        }
    }
}
</style>
